import { useMemo } from 'react';
import { DollarSign, TrendingUp } from 'lucide-react';
import { useBrand } from '@/config/brands';
import { useTheme } from '@/contexts/ThemeContext';
import type { LocalScenarioProcedure } from '@/lib/scenarioStorage';

interface CostDashboardProps {
  procedures: LocalScenarioProcedure[];
  phases: string[];
}

export function CostDashboard({ procedures, phases }: CostDashboardProps) {
  const brand = useBrand();
  const isAhp = brand.design === 'civic';
  const { isDark } = useTheme();
  const ahpDark = isAhp && isDark;

  const { totalCost, phaseBreakdown, procedureCount } = useMemo(() => {
    const total = procedures.reduce(
      (sum, p) => sum + p.estimatedCost * p.quantity,
      0
    );

    const byPhase = new Map<string, number>();
    for (const p of procedures) {
      const phase = p.phase || 'Other';
      byPhase.set(phase, (byPhase.get(phase) || 0) + p.estimatedCost * p.quantity);
    }

    return {
      totalCost: total,
      phaseBreakdown: byPhase,
      procedureCount: procedures.length,
    };
  }, [procedures]);

  if (procedures.length === 0) return null;

  const maxPhaseCost = Math.max(...Array.from(phaseBreakdown.values()));

  return (
    <div className={`rounded-xl border p-4 ${
      ahpDark
        ? 'border-civic-gold/20 bg-gradient-to-r from-civic-card-dark to-[#0f2030]'
        : isAhp
        ? 'border-gray-200 bg-gradient-to-r from-green-50 to-emerald-50'
        : 'border-matrix-green/20 bg-black/30'
    }`}>
      {/* Total cost header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
            isAhp ? 'bg-civic-navy/10' : 'bg-matrix-green/10'
          }`}>
            <DollarSign className={`w-4 h-4 ${isAhp ? 'text-civic-navy' : 'text-matrix-green'}`} />
          </div>
          <div>
            <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Estimated Total</p>
            <p className={`text-xl font-mono font-bold ${isAhp ? 'text-civic-navy' : 'text-matrix-green'}`}>
              ${totalCost.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
            </p>
          </div>
        </div>
        <div className="text-right">
          <p className="text-[10px] text-muted-foreground">{procedureCount} procedure{procedureCount !== 1 ? 's' : ''}</p>
          <p className="text-[10px] text-muted-foreground">{phases.length} phase{phases.length !== 1 ? 's' : ''}</p>
        </div>
      </div>

      {/* Phase cost bars */}
      {phaseBreakdown.size > 1 && (
        <div className="space-y-1.5">
          {Array.from(phaseBreakdown.entries()).map(([phase, cost]) => {
            const pct = maxPhaseCost > 0 ? (cost / maxPhaseCost) * 100 : 0;
            return (
              <div key={phase} className="flex items-center gap-2">
                <span className={`text-[10px] w-24 truncate text-right ${
                  isAhp ? 'text-gray-600' : 'text-muted-foreground'
                }`}>
                  {phase}
                </span>
                <div className={`flex-1 h-3 rounded-full overflow-hidden ${
                  ahpDark ? 'bg-white/10' : isAhp ? 'bg-gray-200' : 'bg-white/5'
                }`}>
                  <div
                    className={`h-full rounded-full transition-all ${
                      isAhp ? 'bg-civic-navy' : 'bg-matrix-green'
                    }`}
                    style={{ width: `${Math.max(pct, 3)}%` }}
                  />
                </div>
                <span className={`text-[10px] font-mono w-16 text-right ${
                  isAhp ? 'text-gray-700' : 'text-foreground/70'
                }`}>
                  ${cost >= 1000 ? `${(cost / 1000).toFixed(1)}k` : cost.toLocaleString()}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
